/**
 * Currency utility functions for the application
 */

/**
 * Fetches the current CAD to USD exchange rate from an external API
 * @returns {Promise<number>} The exchange rate (CAD to USD)
 */
export const fetchCadToUsdRate = async () => {
  try {
    // Using exchangerate-api.com as the source for exchange rates
    const response = await fetch(
      'https://v6.exchangerate-api.com/v6/4f06c51fccc82aa98028bc5b/latest/CAD'
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rate');
    }
    
    const data = await response.json();
    
    // Return the CAD to USD rate
    return data.conversion_rates.USD;
  } catch (error) {
    console.error('Error fetching exchange rate:', error);
    // Return fallback rate of 0.74 if API call fails
    return 0.74;
  }
};

/**
 * Fetches and stores the exchange rate in local storage with an expiration time
 * to minimize API calls and provide a fallback when offline
 */
export const updateExchangeRate = async () => {
  try {
    // Check if we have a cached rate and if it's still valid (less than 24 hours old)
    const cachedData = localStorage.getItem('exchangeRateData');
    
    if (cachedData) {
      const { rate, timestamp } = JSON.parse(cachedData);
      const now = new Date().getTime();
      const cacheAge = now - timestamp;
      
      // If cache is less than 24 hours old, use the cached rate
      if (cacheAge < 24 * 60 * 60 * 1000) {
        return rate;
      }
    }
    
    // Fetch new rate if cache is expired or doesn't exist
    const newRate = await fetchCadToUsdRate();
    
    // Store the new rate with current timestamp
    localStorage.setItem(
      'exchangeRateData',
      JSON.stringify({
        rate: newRate,
        timestamp: new Date().getTime()
      })
    );
    
    return newRate;
  } catch (error) {
    console.error('Error updating exchange rate:', error);
    
    // If we have any cached data, use it regardless of age
    const cachedData = localStorage.getItem('exchangeRateData');
    if (cachedData) {
      return JSON.parse(cachedData).rate;
    }
    
    // Last resort fallback
    return 0.74;
  }
};

/**
 * Gets the current exchange rate, either from cache or by fetching a new one
 * @returns {Promise<number>} The CAD to USD exchange rate
 */
export const getCurrentExchangeRate = async () => {
  return await updateExchangeRate();
};
